import { defineStore } from 'pinia';
import { LocalStorage } from 'quasar';
import type { dynamicObj } from 'src/pages/assets';
import { invoiceDashboardService } from 'src/services';
import { useAmountFormatStore, useSettingStore } from '../main';
import { invoice_dashboard_columns_setting } from 'src/constants';
import { getSavedColumns as getColumns, saveColumnOrder as saveColumns } from 'src/utils/columnSettingsUtil';

export const invoiceDashboardStore = defineStore('invoice_dashboard_store', {
  state: () => ({
    loading: false,
    parent_id: '',
    display_data: [] as dynamicObj,
    invoice_details: {} as dynamicObj,
    preview_path: '',
    invoice_id_path: '',
    total_count: 0,
    total_invoice_count: 0,
    total_closed_count: 0,
    params: {
      search: '',
      status: '',
      paid_status: '',
      sort_by: '',
      sort_order: '',
      supplier_name: '',
    },
    order_by: {} as dynamicObj,
    quickFilterMap: new Map(),
    table_columns: {
      alternate_document_id: {
        name: 'alternate_document_id',
        translation_key: 'invoice_number',
        actions: true,
        field: 'alternate_document_id',
        order: 0,
      },
      paid_status: {
        name: 'paid_status',
        translation_key: 'paid_status',
        actions: true,
        field: 'paid_status',
        order: 1,
      },
      supplier_name: {
        name: 'supplier_name',
        translation_key: 'supplier_name',
        actions: true,
        field: 'supplier_name',
        order: 2,
      },
      supplier_id: {
        name: 'supplier_id',
        translation_key: 'supplier_number',
        actions: true,
        field: 'supplier_id',
        order: 3,
      },
      extended_amount: {
        name: 'extended_amount',
        translation_key: 'amount',
        actions: true,
        field: 'extended_amount',
        order: 4,
      },
      invoice_date: {
        name: 'invoice_date',
        translation_key: 'invoice_details_date',
        actions: true,
        field: 'invoice_date',
        order: 5,
      },
      invoice_due_date: {
        name: 'invoice_due_date',
        translation_key: 'invoice_due_date',
        actions: true,
        field: 'invoice_due_date',
        order: 6,
      },
      discount_due_date: {
        name: 'discount_due_date',
        translation_key: 'discount_due_date',
        actions: true,
        field: 'discount_due_date',
        order: 7,
      },
      discount_term: {
        name: 'discount_term',
        translation_key: 'discount_term',
        actions: true,
        field: 'discount_term',
        order: 8,
      },
      payment_term_description: {
        name: 'payment_term_description',
        translation_key: 'payment_term_description',
        actions: true,
        field: 'payment_term_description',
        order: 9,
      },
      ap_assignment_code: {
        name: 'ap_assignment_code',
        translation_key: 'ap_assignment_code',
        actions: true,
        field: 'ap_assignment_code',
        order: 10,
      },
      purchase_order_number: {
        name: 'purchase_order_number',
        translation_key: 'purchase_order',
        actions: true,
        field: 'purchase_order_number',
        order: 11,
      },
      payment_date: {
        name: 'payment_date',
        translation_key: 'payment_date',
        actions: true,
        field: 'payment_date',
        order: 12,
      },
      payment_method: {
        name: 'payment_method',
        translation_key: 'payment_method',
        actions: true,
        field: 'payment_method',
        order: 13,
      },
      electronic_payment_number: {
        name: 'electronic_payment_number',
        translation_key: 'electronic_payment',
        actions: true,
        field: 'electronic_payment_number',
        order: 14,
      },
      status: {
        name: 'status',
        translation_key: 'status',
        actions: true,
        field: 'status',
        order: 15,
      },
    } as dynamicObj,
    pagination: {
      current_page: 1,
      total_pages: 0,
      page_size: 25,
    },
  }),
  actions: {
    resetPagination() {
      this.pagination = {
        current_page: 1,
        total_pages: 0,
        page_size: 25,
      };
    },
    resetFilter() {
      this.params = {
        search: '',
        status: '',
        paid_status: '',
        sort_by: '',
        sort_order: '',
        supplier_name: '',
      };
      this.pagination = {
        current_page: 1,
        total_pages: 0,
        page_size: 25,
      };
    },
    searchValueByKey(key: string, values: dynamicObj[]) {
      return useAmountFormatStore().searchValueByKey(key, values);
    },
    async getInvoiceData() {
      try {
        this.loading = true;

        const userDetail: any = LocalStorage.getItem('user_details');
        // ✅ FIX: Add null safety to prevent "Cannot read properties of null" errors
        // when user_details or organisations array is missing/empty
        if (!userDetail?.organisations?.[0]) {
          console.warn('[InvoiceDashboard] No organisation data available - user session may be incomplete');
          this.loading = false;
          return;
        }
        this.parent_id = userDetail.organisations[0].parent_org_id
          ?? userDetail.organisations[0].org_id;

        const filterObj: any = {};
        for (const [key, value] of Object.entries(this.params)) {
          const data = value as any;
          if (typeof data === 'object') {
            if (data && Object.keys(data).length > 0) {
              filterObj[key] = data.value;
            }
          } else if (data !== '') {
            filterObj[key] = data;
          }
        }

        const offset =
          (this.pagination.current_page - 1) * this.pagination.page_size;
        const response = await invoiceDashboardService.getInvoiceDashboard({
          ...filterObj,
          parent_id: this.parent_id,
          limit: this.pagination.page_size,
          offset: offset,
        });
        if (!response) {
          throw response;
        }
        this.display_data = response?.data || [];
        this.pagination.total_pages = Math.ceil(
          response.metadata.total / this.pagination.page_size,
        );

        this.total_count = response?.count?.total || 0;
        this.total_invoice_count = response?.count?.Invoiced || 0;
        this.total_closed_count = response?.count?.Closed || 0;

        const values: { key: string | number; value: string | number }[] = [];
        this.display_data.forEach((data: dynamicObj) => {
          if (data.extended_amount) {
            values.push({
              key: data.extended_amount,
              value: data.extended_amount,
            });
          } else {
            values.push({
              key: 0,
              value: 0,
            });
          }
        });

        const settings_store = useSettingStore();
        let dest_locale = settings_store.user_settings.AMOUNTS_LOCALE;
        if (!dest_locale) {
          dest_locale = await settings_store.getSetting('AMOUNTS_LOCALE');
        }
        const results = await useAmountFormatStore().formatMultiValues(
          values,
          'en_US',
          dest_locale,
        );
        this.display_data = this.display_data.map((data: dynamicObj) => {
          return {
            ...data,
            extended_amount: this.searchValueByKey(
              data.extended_amount,
              results,
            ),
          };
        });
      } catch (err: any) {
        logError(err);
      } finally {
        this.loading = false;
      }
    },
    async getInvoiceDetails(id: string) {
      try {
        const response = await invoiceDashboardService.getOne(id);
        if (!response) {
          throw response;
        }
        this.invoice_details = response?.data;
        this.invoice_id_path = response?.data?.alternate_document_id;

        const values = [];
        const extendedAmount = this.invoice_details.extended_amount ?? 0;
        values.push({
          key: extendedAmount,
          value: extendedAmount,
        });

        const settings_store = useSettingStore();
        let dest_locale = settings_store.user_settings.AMOUNTS_LOCALE;
        if (!dest_locale) {
          dest_locale = await settings_store.getSetting('AMOUNTS_LOCALE');
        }
        const results = await useAmountFormatStore().formatMultiValues(
          values,
          'en_US',
          dest_locale,
        );
        this.invoice_details = {
          ...this.invoice_details,
          extended_amount: this.searchValueByKey(extendedAmount, results),
        };
      } catch (err: any) {
        logError(err);
      }
    },
    async getPreviewPath(invoice_id: string) {
      try {
        const userDetail: any = LocalStorage.getItem('user_details');
        const parentId = userDetail?.organisations[0]?.parent_org_id
          ? userDetail?.organisations[0]?.parent_org_id
          : userDetail.organisations[0].org_id;
        const response = await invoiceDashboardService.getInvoicePreviewPath(
          invoice_id,
          { parent_id: parentId },
        );
        if (response.success) {
          this.preview_path = response?.data?.preview_path as string;
        }
      } catch (err: any) {
        logError(err);
      }
    },
    async getInvoiceShareToken(invoice_id: string) {
      try {
        const response =
          await invoiceDashboardService.getShareToken(invoice_id);
        if (response.success) {
          return response?.data?.token as dynamicObj;
        }
      } catch (err: any) {
        logError(err);
      }
    },

    async getSavedColumns() {
      return await getColumns({
        settingKey: invoice_dashboard_columns_setting,
        tableColumns: this.table_columns,
      });
    },

    async saveColumnOrder(columns: any[]) {
      await saveColumns(invoice_dashboard_columns_setting, columns);
    },
  },
});
